import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeLargeCtaSkeleton } from "./TypeLargeCta";
import type { TypeNavigationCardSkeleton } from "./TypeNavigationCard";
import type { TypeSmallCtaSkeleton } from "./TypeSmallCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";

/**
 * Fields type definition for content type 'TypeNavigationCardsSection'
 * @name TypeNavigationCardsSectionFields
 * @type {TypeNavigationCardsSectionFields}
 * @memberof TypeNavigationCardsSection
 */
export interface TypeNavigationCardsSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized false
     */
    header?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized false
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'backgroundColor' (Background Color)
     * @name Background Color
     * @localized false
     */
    backgroundColor: EntryFieldTypes.Symbol<"grey" | "light-blue" | "white">;
    /**
     * Field type definition for field 'description' (Description)
     * @name Description
     * @localized false
     */
    description?: EntryFieldTypes.Text;
    /**
     * Field type definition for field 'navigationCards' (Navigation Cards)
     * @name Navigation Cards
     * @localized false
     */
    navigationCards: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeNavigationCardSkeleton>>;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
    /**
     * Field type definition for field 'cta' (CTA)
     * @name CTA
     * @localized false
     */
    cta?: EntryFieldTypes.EntryLink<TypeLargeCtaSkeleton | TypeSmallCtaSkeleton>;
}

/**
 * Entry skeleton type definition for content type 'navigationCardsSection' (Navigation Cards Section)
 * @name TypeNavigationCardsSectionSkeleton
 * @type {TypeNavigationCardsSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-26T19:53:31.247Z
 * @version 15
 */
export type TypeNavigationCardsSectionSkeleton = EntrySkeletonType<TypeNavigationCardsSectionFields, "navigationCardsSection">;
/**
 * Entry type definition for content type 'navigationCardsSection' (Navigation Cards Section)
 * @name TypeNavigationCardsSection
 * @type {TypeNavigationCardsSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-26T19:53:31.247Z
 * @version 15
 */
export type TypeNavigationCardsSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeNavigationCardsSectionSkeleton, Modifiers, Locales>;

export function isTypeNavigationCardsSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeNavigationCardsSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'navigationCardsSection'
}
